import { useState } from "react";
import { sizedImageUrl, sizedImageSrcSet } from "@/lib/img";

// Property page gallery: hero spans ~half the max-w-7xl container on desktop
// (up to ~630 CSS px → 2000w covers Retina); side tiles are ~quarter width.
const HERO_IMG_WIDTHS = [800, 1600, 2000];
const HERO_IMG_SIZES = "(min-width: 768px) 50vw, 100vw";
const TILE_IMG_WIDTHS = [400, 800];
const TILE_IMG_SIZES = "(min-width: 768px) 25vw, 100vw";

interface PhotoGridProps {
  images: string[];
  propertyName: string;
  onShowAllPhotos: () => void;
}

export default function PhotoGrid({ images, propertyName, onShowAllPhotos }: PhotoGridProps) {
  if (!images || images.length === 0) {
    return (
      <div className="w-full h-64 sm:h-72 md:h-80 lg:h-[500px] bg-gray-200 rounded-lg flex items-center justify-center mb-6 sm:mb-8">
        <div className="text-center text-foreground/70">
          <p>Property images loading...</p>
        </div>
      </div>
    );
  }

  // For mobile, show single hero image
  if (images.length === 1) {
    return (
      <div className="mb-6 sm:mb-8">
        <div className="relative w-full h-64 sm:h-72 md:h-80 lg:h-[500px] rounded-lg overflow-hidden">
          <img
            src={sizedImageUrl(images[0], 1600)}
            srcSet={sizedImageSrcSet(images[0], HERO_IMG_WIDTHS)}
            sizes="100vw"
            alt={`${propertyName}`}
            className="w-full h-full object-cover cursor-pointer hover:brightness-95 transition-all"
            onClick={onShowAllPhotos}
          />
          <button
            onClick={onShowAllPhotos}
            className="absolute bottom-4 right-4 bg-background px-4 py-2 rounded-lg shadow-lg text-sm font-medium text-foreground hover:bg-background/90 transition-colors flex items-center gap-2 border border-border"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Show all photos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6 sm:mb-8">
      {/* Mobile: Single hero image with photo count */}
      <div className="block md:hidden">
        <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden">
          <img
            src={sizedImageUrl(images[0], 800)}
            srcSet={sizedImageSrcSet(images[0], HERO_IMG_WIDTHS)}
            sizes="100vw"
            alt={`${propertyName}`}
            className="w-full h-full object-cover cursor-pointer hover:brightness-95 transition-all"
            onClick={onShowAllPhotos}
          />
          <button
            onClick={onShowAllPhotos}
            className="absolute bottom-4 right-4 bg-background px-4 py-2 rounded-lg shadow-lg text-sm font-medium text-foreground hover:bg-background/90 transition-colors flex items-center gap-2 border border-border"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2z" />
            </svg>
            Show all photos
          </button>
        </div>
      </div>

      {/* Tablet and Desktop: Hero + 4 image grid */}
      <div className="hidden md:block">
        <div className="w-full aspect-[2/1] rounded-lg overflow-hidden">
          <div className="grid grid-cols-4 gap-2 h-full">
            {/* Hero Image - spans 2 columns and full height */}
            <div className="col-span-2 row-span-2">
              <div className="relative w-full h-full">
                <img
                  src={sizedImageUrl(images[0], 1600)}
                  srcSet={sizedImageSrcSet(images[0], HERO_IMG_WIDTHS)}
                  sizes={HERO_IMG_SIZES}
                  alt={`${propertyName} - Main`}
                  className="w-full h-full object-cover cursor-pointer hover:brightness-95 transition-all"
                  onClick={onShowAllPhotos}
                />
              </div>
            </div>

            {/* Grid Images - 2x2 on the right */}
            {images.slice(1, 5).map((image, index) => (
              <div key={index} className="relative aspect-square">
                <img
                  src={sizedImageUrl(image, 800)}
                  srcSet={sizedImageSrcSet(image, TILE_IMG_WIDTHS)}
                  sizes={TILE_IMG_SIZES}
                  alt={`${propertyName} - ${index + 2}`}
                  className="w-full h-full object-cover cursor-pointer hover:brightness-95 transition-all"
                  onClick={onShowAllPhotos}
                />
                {/* Show all photos button on last image */}
                {index === 3 && images.length >= 4 && (
                  <button
                    onClick={onShowAllPhotos}
                    className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center text-white font-medium hover:bg-opacity-40 transition-all"
                  >
                    <div className="flex items-center gap-2">
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2z" />
                        </svg>
                      Show all photos
                    </div>
                  </button>
                )}
              </div>
            ))}

            {/* Fill empty slots if we have fewer than 5 images */}
            {Array.from({ length: Math.max(0, 4 - (images.length - 1)) }).map((_, index) => (
              <div key={`empty-${index}`} className="bg-gray-200 aspect-square"></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}